import {useState, useEffect, useCallback} from 'react';
import * as CandidatesApi from '../api';

const useMergeHistory = candidateId => {
  const [mergeHistory, setMergeHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [revertError, setRevertError] = useState(null);

  const fetchMergeHistory = useCallback(async () => {
    if (!candidateId) return;

    setLoading(true);
    setError(false);
    try {
      const response = await CandidatesApi.getMergeHistory(candidateId);
      const historyData = response.data || response;

      // API returns empty array if no merge history
      setMergeHistory(historyData.length > 0 ? historyData : null);
    } catch (err) {
      console.error('Error fetching merge history:', err);
      setMergeHistory(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [candidateId]);

  const revertMerge = useCallback(async () => {
    if (!candidateId) return null;

    setReverting(true);
    setRevertError(null);
    try {
      const response = await CandidatesApi.revertMerge(candidateId);
      const result = response.data || response;

      if (result.success) {
        // Clear merge history after successful revert
        setMergeHistory(null);
        return {
          success: true,
          primeCandidateId: result.prime_candidate_id,
          dupeCandidateId: result.dupe_candidate_id,
        };
      }
      setRevertError(result.error || 'Failed to revert merge');
      return {success: false, error: result.error};
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to revert merge';
      console.error('Error reverting merge:', err);
      setRevertError(errorMessage);
      return {success: false, error: errorMessage};
    } finally {
      setReverting(false);
    }
  }, [candidateId]);

  useEffect(() => {
    fetchMergeHistory();
  }, [fetchMergeHistory]);

  return {
    mergeHistory,
    loading,
    error,
    reverting,
    revertError,
    fetchMergeHistory,
    revertMerge,
    hasMergeHistory: mergeHistory && mergeHistory.length > 0,
  };
};

export default useMergeHistory;
